/**
 * PdfViewerModal Component
 * Modal for previewing and downloading PDF reports
 */
import React from 'react';

interface PdfViewerModalProps {
  isOpen: boolean;
  pdfUrl: string | null;
  version: string;
  onClose: () => void;
  onDownload: () => void;
}

export const PdfViewerModal: React.FC<PdfViewerModalProps> = ({
  isOpen,
  pdfUrl,
  version,
  onClose,
  onDownload
}) => {
  if (!isOpen || !pdfUrl) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Modal Header */}
        <ModalHeader 
          version={version} 
          onClose={onClose} 
          onDownload={onDownload} 
        />
        
        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden bg-gray-200">
          <iframe
            src={pdfUrl}
            className="w-full h-full"
            title="Rapport PDF"
          />
        </div>
        
        {/* Modal Footer */}
        <ModalFooter onClose={onClose} />
      </div>
    </div>
  );
};

// Modal header component
interface ModalHeaderProps {
  version: string;
  onClose: () => void;
  onDownload: () => void;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ version, onClose, onDownload }) => (
  <div className="flex justify-between items-center p-4 border-b bg-gray-50 rounded-t-xl">
    <div className="flex items-center gap-3">
      <span className="text-2xl"></span>
      <div>
        <h3 className="font-bold text-gray-800">Rapport de Profil</h3>
        <p className="text-sm text-gray-500">SkillSprint Challenge - Version {version}</p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onDownload}
        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg text-sm transition"
      >
        <DownloadIcon />
        Télécharger PDF
      </button>
      <button
        onClick={onClose}
        className="p-2 hover:bg-gray-200 rounded-lg transition"
        title="Fermer"
      >
        <CloseIcon />
      </button>
    </div>
  </div>
);

// Modal footer component
interface ModalFooterProps {
  onClose: () => void;
}

const ModalFooter: React.FC<ModalFooterProps> = ({ onClose }) => (
  <div className="p-3 border-t bg-gray-50 rounded-b-xl flex justify-between items-center">
    <p className="text-xs text-gray-500">
      Généré le {new Date().toLocaleDateString('fr-FR')} à {new Date().toLocaleTimeString('fr-FR')}
    </p>
    <button
      onClick={onClose}
      className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg text-sm transition"
    >
      Fermer
    </button>
  </div>
);

// Icon components
const DownloadIcon: React.FC = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d="M6 18L18 6M6 6l12 12" 
    />
  </svg>
);

export default PdfViewerModal;
